import React, { memo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { List, ListItemButton, ListItemText, Collapse, ListItem } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';

import NavbarMenu from './NavbarMenu';
import { getActivePathValue, getPathDestination } from './Navbar';
import { stripQuery } from '../../resources/helper';

const CollapsedList = styled(List, { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'toTop' })(({ theme, open, toTop }) => ({
  margin: '0 4px',
  background: '#004A4E',
  borderRadius: open ? (toTop ? '5px 5px 0 0' : '0 0 5px 5px') : 5,
  padding: 5,

  '& .MuiListItemButton-root': {
    marginTop: 5,
    borderRadius: 5,
    fontSize: 12,
    height: 25,
    padding: '0 5px',
  },

  '& .MuiListItemButton-root:first-child': {
    marginTop: 0,
  },

  '& .MuiListItemButton-root.Mui-selected': {
    background: '#fff',
    color: '#275E63',
  },
}));

const NavbarMainButton = memo(
  ({ Icon, opened, openedMenu, path, onClick, selected, items, label, onMenuClick, toTop, overlapIconText }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [menuStateOpened, setMenuStateOpened] = useState([]);

    const activePathValue = getActivePathValue(location);

    const hasChildren = items && items.length > 0;

    const toggleSubMenu = (menuItem, items) => {
      const menuItemsAr = [menuItem, ...items.map((item) => item.label)];

      if (menuStateOpened.indexOf(menuItem) === -1) {
        setMenuStateOpened([...menuStateOpened, menuItem]);
      } else {
        setMenuStateOpened(menuStateOpened.filter((item) => menuItemsAr.indexOf(item) === -1));
      }
    };

    const isOpenedMenuItem = (menuItem) => menuStateOpened.indexOf(menuItem) !== -1;

    const handleMenuClick = ({ label, path, items, onClick, subItem = false }) => {
      if (items && items.length > 0) {
        toggleSubMenu(label, items);

        return;
      }

      if (onClick) {
        onClick();
      }

      if (path) {
        navigate(path);
      }
    };

    const getExpandButton = (hasChildren, openedMenu) => {
      if (!hasChildren) {
        return null;
      }

      if (openedMenu) {
        return toTop ? <ExpandMore /> : <ExpandLess />;
      }

      return toTop ? <ExpandLess /> : <ExpandMore />;
    };

    const getCollapsedContent = () => {
      if (!hasChildren || !opened) {
        return null;
      }

      return (
        <Collapse in={openedMenu} timeout="auto" unmountOnExit>
          <CollapsedList
            open={openedMenu}
            component="div"
            disablePadding
            toTop={toTop}
            className={`collapsed-menu ${toTop ? ' totop' : ''}`}
          >
            {items
              .filter((item) => get(item, 'available', true))
              .map(({ disabled, path, label: childLabel, items, CustomComponent }, index) => {
                const itemPath = getPathDestination(path);
                const openedMenu = isOpenedMenuItem(childLabel);
                const hasChildren = items && items.length;
                const selected = hasChildren
                  ? items.some((item) => stripQuery(getPathDestination(item.path)) === activePathValue)
                  : stripQuery(itemPath) === activePathValue;

                if (CustomComponent) {
                  return (
                    <ListItem key={`${index}_custom`} className="custom-component">
                      <CustomComponent opened={opened} />
                    </ListItem>
                  );
                }

                return (
                  <>
                    <ListItemButton
                      key={`${label}_${childLabel}`}
                      sx={{ pl: 4 }}
                      disabled={disabled}
                      selected={selected}
                      onClick={() => handleMenuClick({ label: childLabel, path, items })}
                    >
                      <ListItemText primary={childLabel} />
                      {getExpandButton(hasChildren, openedMenu)}
                    </ListItemButton>
                    {hasChildren && (
                      <Collapse in={openedMenu} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {items.map(({ disabled, path, label: childLabel }, index) => {
                            const itemPath = getPathDestination(path);

                            return (
                              <ListItemButton
                                key={`${label}_${childLabel}`}
                                sx={{ pl: 4 }}
                                disabled={disabled}
                                selected={itemPath === activePathValue}
                                onClick={() => handleMenuClick({ path })}
                              >
                                <ListItemText primary={childLabel} />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </>
                );
              })}
          </CollapsedList>
        </Collapse>
      );
    };

    return (
      <>
        {toTop && getCollapsedContent()}
        <NavbarMenu
          Icon={Icon}
          selected={selected}
          key={label}
          label={label}
          overlapIconText={overlapIconText}
          opened={opened}
          openedMenu={openedMenu}
          toTop={toTop}
          items={items}
          onClick={() => {
            handleMenuClick({
              label,
              path,
              items,
              onClick,
            });
            onMenuClick();
          }}
        />
        {!toTop && getCollapsedContent()}
      </>
    );
  },
);

export default NavbarMainButton;
